// Lesson blocks (opt-in exercises inside a lesson): the shapes the server
// sends, and the small text helpers the editor and the learner screens share.
// The server is the judge (schemas, syllable rules, grading); these helpers
// only make typing and previewing pleasant.

export type Haraka = "fatha" | "kasra" | "damma"

export const HARAKAT: Record<Haraka, string> = { fatha: "َ", kasra: "ِ", damma: "ُ" }
const MARK_TO_HARAKA: Record<string, Haraka> = { "َ": "fatha", "ِ": "kasra", "ُ": "damma" }
export const TATWEEL = "ـ"
const LETTER = /^[ء-غف-ي]$/

export type BlockKind =
  | "letter_intro"
  | "letter_forms"
  | "harakat_table"
  | "syllable_grid"
  | "transliterate"
  | "freehand_write"
  | "listen_and_choose"
  | "record_and_compare"
  | "put_in_order"
  | "word_list"
  | "tracing"
  | "sign_table"
  | "word_transliterate"
  | "pick_all"
  | "alphabet_chart"
  | "page_recording"

export type BlockData = Record<string, unknown>

/** Romanization table: haraka → Latin letter ("book": fatha = E). */
export type Romanization = Record<Haraka, string>

/** Split typed syllables on spaces (and the Arabic comma). */
export function splitSyllables(text: string): string[] {
  return text
    .split(/[\s،,]+/)
    .map((part) => part.normalize("NFC"))
    .filter(Boolean)
}

export interface SyllableParts {
  letter: string
  haraka: Haraka | null
  joined: boolean
  /** Marks other than one short vowel (two vowels, sukun, shadda…). */
  extraMarks: number
}

/** Letter + marks of one syllable, for the "spell it out" preview. */
export function syllableParts(text: string): SyllableParts | null {
  let value = text.normalize("NFC").trim()
  const joined = value.startsWith(TATWEEL)
  if (joined) value = value.slice(1)
  const [letter, ...marks] = Array.from(value)
  if (!letter || !LETTER.test(letter)) return null
  const vowels = marks.filter((mark) => mark in MARK_TO_HARAKA)
  // Sukun, shadda, tanwin, or a second letter: anything but one short vowel.
  const others = marks.filter((mark) => !(mark in MARK_TO_HARAKA))
  return {
    letter,
    haraka: vowels.length === 1 ? MARK_TO_HARAKA[vowels[0]] : null,
    joined,
    extraMarks: Math.max(vowels.length - 1, 0) + others.length,
  }
}

/** One letter with exactly one short vowel: what the server accepts. */
export function isSyllable(text: string): boolean {
  const parts = syllableParts(text)
  return !!parts && parts.haraka !== null && parts.extraMarks === 0
}

/** "E-U" / "e u" / "E–U" → ["fatha", "damma"] with the course's table; null when a letter is unknown. */
export function parseLatinItem(text: string, table: Romanization): Haraka[] | null {
  const letters = text
    .toUpperCase()
    .split(/[\s\-–—,]+/)
    .filter(Boolean)
  if (letters.length === 0) return null
  const byLatin = Object.fromEntries(Object.entries(table).map(([haraka, latin]) => [latin.toUpperCase(), haraka])) as Record<string, Haraka>
  const harakat = letters.map((letter) => byLatin[letter])
  return harakat.every(Boolean) ? harakat : null
}

/** ["fatha", "damma"] → "E-U". */
export function formatLatinItem(harakat: Haraka[], table: Romanization): string {
  return harakat.map((haraka) => table[haraka]).join("-")
}

/** One syllable from a letter and a mark (a hamza seat changes Elif only). */
export function composeSyllable(letter: string, haraka: Haraka, seat: "bare" | "hamza" = "bare"): string {
  const base = letter === "ا" && seat === "hamza" ? (haraka === "kasra" ? "إ" : "أ") : letter
  return `${base}${HARAKAT[haraka]}`
}

/** Lines of a textarea, trimmed, blanks dropped. */
export function lines(text: string): string[] {
  return text
    .split("\n")
    .map((line) => line.trim())
    .filter(Boolean)
}

/** Shuffle a copy deterministically (the same seed gives the same order). */
export function shuffled<T>(items: T[], seed: number): T[] {
  const copy = [...items]
  let state = seed || 1
  for (let i = copy.length - 1; i > 0; i--) {
    state = (state * 1103515245 + 12345) % 2147483648
    const j = state % (i + 1)
    ;[copy[i], copy[j]] = [copy[j], copy[i]]
  }
  return copy
}
